import { Colors } from "../../../../utils/colors";
import "../../../../css/MassTexts.css";
import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  AccountData,
  MassText,
  MassTextAttachment,
  TrackingOverview,
} from "@markit/common.types";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import {
  getUserCampaignData,
  getUserData,
} from "../../../../utils/FirebaseUtils";
import { getEventState } from "../../../../redux/slices/eventSlice";
import { useLoadTextRecipients } from "../../../../hooks/useLoadTextRecipients";
import SearchBoxContainer from "../../../Containers/SearchBoxContainer";
import filter from "lodash.filter";
import VirtuosoList from "../../../Lists/VirtuosoList";
import { CircularProgress } from "@mui/material";
import { handlePluralString, objectToMap } from "@markit/common.utils";
import { API } from "../../../../API";
import { getNumMassTextRecipients } from "../../../../utils/textingUtils";
import { MassTextLinkAnalyticsItem } from "../Items/MassTextLinkAnalyticsItem";
import {
  calculateRevenueFromTextConversions,
  getMassTextAttachmentsLinkAnalytics,
  showTextConversions,
} from "../../../../utils/trackingUtils";
import useAsyncOnMount from "../../../../hooks/useAsyncEffectOnMount";
import { useOnMount } from "../../../../hooks/useOnMount";
import StandardBorderedContainer from "../../../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import EventItemImage from "../../../DisplayItem/EventItem/EventItemImage";
import { FormatCurrency } from "../../../../utils/FormatCurrency";

type MassTextsLinkAnalyticsProps = {
  massText: MassText;
  massTextAttachment: MassTextAttachment;
};

const MassTextsLinkAnalytics = (props: MassTextsLinkAnalyticsProps) => {
  const { massText, massTextAttachment } = props;
  const { account } = useSelector(getAccountState);
  const { accountData } = account;
  const { events } = useSelector(getEventState);
  const { events: userEvents } = events;
  const [numClicks, setNumClicks] = useState(0);
  const [numConversions, setNumConversions] = useState(0);
  const [openRate, setOpenRate] = useState(0);
  const [conversionRate, setConversionRate] = useState(0);
  const [conversionsRevenue, setConversionsRevenue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRecipient, setSelectedRecipient] = useState<AccountData>();
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  const styles = {
    rowContainer: { paddingInline: 14, gap: 14 },
  };

  const {
    isFinished,
    isLoading,
    fetchedUserData,
    fetchedTextRecipients,
    loadTextRecipients,
    loadMoreTextRecipients,
    loadSearchResultsThrottled,
  } = useLoadTextRecipients({
    userId: massText.sentBy,
    massTextId: massText.id,
    campaignId: massText.campaignId,
    attachmentId: massTextAttachment.id,
    windowSize: 30,
  });

  useAsyncOnMount(async () => {
    let finalUserData: AccountData = accountData;
    // for viewing other user's texting analytics
    if (massText.sentBy !== accountData.uid) {
      const userData = await getUserData(massText.sentBy);
      if (userData) {
        finalUserData = userData;
      }
    }

    const campaign = await getUserCampaignData(
      finalUserData.uid,
      massText.campaignId
    );
    if (campaign) {
      const numRecipientsPromise = getNumMassTextRecipients(
        massText.sentBy,
        campaign,
        massText.id
      );
      const attachmentAnalyticsPromise = getMassTextAttachmentsLinkAnalytics(
        massText.sentBy,
        massText,
        massTextAttachment
      );
      const conversionsRevenuePromise = calculateRevenueFromTextConversions(
        massText,
        massTextAttachment
      );

      const numRecipients = await numRecipientsPromise;
      const ratesPromise = Promise.all([
        API.tracking.getAttachmentOpenRate({
          userId: massText.sentBy,
          massTextId: massText.id,
          campaignId: massText.campaignId,
          attachmentId: massTextAttachment.id,
          numRecipients,
        }),
        API.tracking.getAttachmentConversionRate({
          userId: massText.sentBy,
          massTextId: massText.id,
          campaignId: massText.campaignId,
          attachmentId: massTextAttachment.id,
          numRecipients,
        }),
      ]);

      const [{ numOpens, numConversions }, responses, totalConversionsRevenue] =
        await Promise.all([
          attachmentAnalyticsPromise,
          ratesPromise,
          conversionsRevenuePromise,
        ]);

      setNumClicks(numOpens);
      setNumConversions(numConversions);
      setOpenRate(responses[0].totalNum);
      setConversionRate(responses[1].totalNum);
      setConversionsRevenue(totalConversionsRevenue);
    }

    setLoadingAnalytics(false);
  });

  useOnMount(() => {
    loadTextRecipients();
  });

  const contains = useCallback((user: AccountData, query: string) => {
    return user.fullName.toLowerCase().includes(query);
  }, []);

  const handleSearch = (text: string) => {
    setSearchTerm(text.toLowerCase());
    loadSearchResultsThrottled(text.toLowerCase());
  };

  const itemsToShow: AccountData[] = useMemo(() => {
    let userList: AccountData[] = [];
    userList = fetchedUserData;
    if (searchTerm !== "") {
      userList = filter(userList, (user: AccountData) => {
        return contains(user, searchTerm);
      });
    }
    return userList;
  }, [fetchedUserData, searchTerm, contains]);

  const renderEventItemPreview = useCallback(
    (attachment: MassTextAttachment) => {
      const foundEvent = userEvents.find(
        (event) => event.id === attachment.eventId
      );
      return (
        <div className="AlignedRow" style={{ gap: 8 }}>
          <EventItemImage
            item={foundEvent}
            size={40}
            containerStyles={{ backgroundColor: Colors.GRAY6 }}
          />
          <div className="ColumnNormal">
            <span style={{ fontSize: 14, fontWeight: "500" }}>
              {attachment.massTextType}
            </span>
            <span
              style={{
                fontSize: 14,
                color: Colors.GRAY2,
              }}
            >
              {foundEvent?.title ?? "Markit Link"}
            </span>
          </div>
        </div>
      );
    },
    [userEvents]
  );

  const foundEvent = useMemo(
    () => userEvents.find((event) => event.id === massTextAttachment.eventId),
    [massTextAttachment.eventId, userEvents]
  );

  const showConversions = useMemo(
    () => showTextConversions(foundEvent, massTextAttachment),
    [foundEvent, massTextAttachment]
  );

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollParent, setScrollParent] = useState<HTMLDivElement | null>(null);

  return (
    <div
      ref={(el) => {
        scrollContainerRef.current = el;
        setScrollParent(el);
      }}
      className="ColumnNormal AllowScroll"
      style={{
        padding: 20,
        paddingBottom: 0,
        flex: 1,
        minHeight: 0,
        position: "relative",
      }}
    >
      <StandardBorderedContainer
        containerStyles={{
          paddingBlock: 14,
          marginBottom: 14,
          backgroundColor: Colors.WHITE1,
        }}
      >
        <div style={styles.rowContainer} className="ColumnNormal">
          {renderEventItemPreview(massTextAttachment)}
        </div>
        <HorizontalDivider altMargin={14} />
        <div style={styles.rowContainer} className="AlignedRow">
          <div className="AlignedRow" style={{ gap: 7 }}>
            <span className="sectionTitle">
              {loadingAnalytics ? "--" : numClicks}
            </span>
            <span className="bodySubtext">
              {handlePluralString("Click", numClicks)}
            </span>
          </div>
          <div className="AlignedRow" style={{ gap: 7 }}>
            <span className="sectionTitle">
              {loadingAnalytics ? "--" : openRate + "%"}
            </span>
            <span className="bodySubtext">Click Rate</span>
          </div>
        </div>
        {showConversions ? (
          <>
            <HorizontalDivider altMargin={14} />
            <div style={styles.rowContainer} className="AlignedRow">
              <div className="AlignedRow" style={{ gap: 7 }}>
                <span className="sectionTitle">
                  {loadingAnalytics ? "--" : numConversions}
                </span>
                <span className="bodySubtext">
                  {handlePluralString("Conversion", numConversions)}
                </span>
              </div>
              <div className="AlignedRow" style={{ gap: 7 }}>
                <span className="sectionTitle">
                  {loadingAnalytics ? "--" : conversionRate + "%"}
                </span>
                <span className="bodySubtext">Conversion Rate</span>
              </div>
            </div>
          </>
        ) : null}
        {!loadingAnalytics && conversionsRevenue > 0 ? (
          <>
            <HorizontalDivider altMargin={14} />
            <div style={styles.rowContainer} className="AlignedRow">
              <div className="AlignedRow" style={{ gap: 7 }}>
                <span className="sectionTitle">
                  {loadingAnalytics ? "--" : FormatCurrency(conversionsRevenue)}
                </span>
                <span className="bodySubtext">
                  Total revenue made from text
                </span>
              </div>
            </div>
          </>
        ) : null}
      </StandardBorderedContainer>
      <div style={{ position: "sticky", top: 0, zIndex: 1 }}>
        <SearchBoxContainer
          placeholder="Search"
          onChange={(e) => handleSearch(e.target.value)}
          value={searchTerm}
          containerStyles={{ backgroundColor: Colors.WHITE1, marginTop: 0 }}
        />
      </div>
      <div
        style={{
          marginTop: 14,
          paddingBottom: 100,
          flex: 1,
          minHeight: 0,
        }}
      >
        <VirtuosoList
          items={itemsToShow}
          loadMoreOnPress={loadMoreTextRecipients}
          customScrollParent={scrollParent}
          renderItem={(user) => {
            const textRecipientData = fetchedTextRecipients.find(
              (recipient) => recipient.uid === user.uid
            );
            if (!textRecipientData) return <div style={{ height: 60 }} />;
            const trackingOverview: TrackingOverview = objectToMap(
              textRecipientData.trackingOverviews
            ).get(massTextAttachment.id);
            return (
              <MassTextLinkAnalyticsItem
                item={user}
                onPress={() => {
                  setSelectedRecipient(
                    selectedRecipient && selectedRecipient.uid === user.uid
                      ? undefined
                      : user
                  );
                }}
                massText={massText}
                attachmentId={massTextAttachment.id}
                isSelected={
                  selectedRecipient ? selectedRecipient.uid === user.uid : false
                }
                trackingOverview={trackingOverview}
                showConversions={showConversions}
              />
            );
          }}
          isLoading={isLoading}
          isFinished={isFinished}
          searchTerm={searchTerm}
          renderNonSearchEmptyView={
            <CircularProgress
              style={{ color: "#929292", alignSelf: "center" }}
              size={20}
            />
          }
          renderHeader={null}
        />
      </div>
    </div>
  );
};

export default MassTextsLinkAnalytics;
